import * as Sentry from '@sentry/react';

import { PROPERTY_ENV } from '../const';
import { dateToIsoDateString } from '../dateUtils';
import { BillingCycleType, ConversionStageType, UserProperties } from './events';

// ###################################################################################
// # LOCAL UTILITY FUNCTIONS                                                         #
// ###################################################################################

/**
 * Initializes Sentry for error tracking and performance monitoring.
 *
 * - Sets the DSN from Sentry project settings.
 * - Configures the environment ('prod' or 'dev') based on the URL.
 * - Enables browser tracing and session replay integrations.
 * - Sets performance monitoring sample rates.
 * - Configures session replay sample rates.
 * - Disables default integrations to avoid automatic error tracking, allowing for more control.
 */
export function initSentry() {
  Sentry.init({
    dsn: 'https://e38ea3a3588658e08c21550a8002a165@o4507397770903552.ingest.de.sentry.io/4507397772607568',
    environment: PROPERTY_ENV,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      '192.168',
      'aiter-ai-dev.web.app',
      'aiter.io',
      'dev.aiter.io',
      'aiter-ai-dev.firebaseapp.com',
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    defaultIntegrations: false, // Disable automatic error tracking
  });
}

// ###################################################################################
// # TRACKING ERRORS FUNCTIONS                                                       #
// ###################################################################################

/**
 * Capture and send error details to Sentry.
 *
 * @param error - Error message to log
 * @param customParams - Additional metadata tags for Sentry
 */
export function trackSentryError(
  error: string,
  customParams: { [key: string]: any } = {}
) {
  console.error(`trackSentryError: ${error}`, customParams);
  Sentry.captureException(new Error(error), {
    tags: customParams,
  });
}

// ###################################################################################
// # USER PROPERTIES FUNCTIONS                                                       #
// ###################################################################################

const CONTEXT_VAR_USER_PROPERTIES = 'user_properties';

/**
 * Sets the Sentry user context.
 *
 * If the user object is null, it clears the existing Sentry user.
 * Otherwise, it sets the user's ID, email, and username and also
 * stores the user ID in a separate context for easier querying.
 *
 * @param user - The user object or null.
 * @param user.id - The user's unique identifier.
 */
export function setSentryUser(user: { id: string } | null) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
  });
  Sentry.setContext(CONTEXT_VAR_USER_PROPERTIES, { [UserProperties.USER_ID]: user.id });
}

/**
 * Sets the user's signup date in the Sentry context.
 *
 * This function captures the current date, formats it to an ISO string (YYYY-MM-DD),
 * and sets it as the `signup_date` in the `user_properties` context.
 */
export function setSentryUserSignupDate() {
  Sentry.setContext(CONTEXT_VAR_USER_PROPERTIES, {
    [UserProperties.SIGNUP_DATE]: dateToIsoDateString(new Date()),
  });
}

/**
 * Sets the user's conversion stage as a tag in Sentry.
 *
 * Tags are indexed and searchable, making them ideal for filtering events.
 *
 * @param conversionStage - A string representing the user's current conversion stage (e.g., "free," "paid," "trial").
 */
export function setSentryUserConversionStage(conversionStage: ConversionStageType) {
  Sentry.setTag(UserProperties.CONVERSION_STAGE, conversionStage);
}

/**
 * Sets the user's signup source as a tag in Sentry for quick filtering.
 *
 * @param source - A string indicating where the user signed up from (e.g., "organic," "ads," "referral").
 */
export function setSentryUserSignupSource(source: string) {
  Sentry.setTag(UserProperties.SIGNUP_SOURCE, source);
}

/**
 * Sets the user's last active date in the Sentry context.
 *
 * This should be called whenever the user performs a meaningful action in the app.
 */
export function setSentryUserLastActiveDate() {
  Sentry.setContext(CONTEXT_VAR_USER_PROPERTIES, {
    [UserProperties.LAST_ACTIVE_DATE]: dateToIsoDateString(new Date()),
  });
}

/**
 * Sets the user's billing cycle as a tag in Sentry.
 *
 * @param cycle - A string representing the billing cycle (e.g., "monthly," "annual").
 */
export function setSentryUserBillingCycle(cycle: BillingCycleType) {
  Sentry.setTag(UserProperties.BILLING_CYCLE, cycle);
}
